import React, { useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import { confirmDialog } from 'primereact/confirmdialog';
import { useLocation, useHistory } from 'react-router-dom';
import queryString from 'query-string';

import constants from 'constants/index';
import { useCalendarUI } from '../../context/CalendarUIContext';
import { useEventMutations } from '../../hooks/useEventMutations';
import { useSingleEvent } from '../../hooks/useSingleEvent';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { useNavbarContext } from 'context/NavbarContext';
import { EventDetailsView, TaskDetailsView } from './ViewComponents';
import { ModalHeader } from 'shared/ModalHeader';
import { getTenantDetails } from 'utils/utils';
import { queryClient } from 'config/queryClient';
import { queryKeys } from 'hooks/config/queryClient';
import moment from 'moment-timezone';
import { calculateRecurringInstanceDates } from '../../utils/eventFormatters';

const EventViewModal = () => {
  const location = useLocation();
  const history = useHistory();
  const { addToast } = useToast();
  const { showConfirmDialog } = useNavbarContext();
  const menuRef = useRef(null);

  const { modals, selectedEventId, selectedEventMetadata, closeEventViewModal, openEventFormModal, openEventViewModal } = useCalendarUI();

  const { userDetails, getPermission, getFirmPermission } = useUserDetailsContext();
  const { deleteEvent, isDeleting } = useEventMutations();

  // Parse URL parameters
  const queryParams = queryString.parse(location.search);
  const itemIdFromUrl = queryParams.id;

  // Determine which ID to use (URL takes precedence)
  const activeItemId = itemIdFromUrl || selectedEventId;

  // ALWAYS fetch event/task data from API using only the ID
  const {
    data: itemData,
    isLoading,
    isError,
    error,
  } = useSingleEvent(
    activeItemId,
    selectedEventMetadata,
    !!activeItemId // Only fetch when we have an ID
  );

  // Determine if this is an event or task based on res_type
  const isTask = itemData?.res_type === 'task';
  const isEvent = itemData?.res_type === 'event' || itemData?.res_type === 'event_rrule';

  // Handle URL-based viewing - open modal if URL has ID
  useEffect(() => {
    if (itemIdFromUrl && !modals.eventView) {
      openEventViewModal(itemIdFromUrl);
    }
  }, [itemIdFromUrl, modals.eventView, openEventViewModal]);

  // Close modal and clear URL parameter
  const handleClose = () => {
    closeEventViewModal();

    // Remove 'id' parameter from URL if present
    if (itemIdFromUrl) {
      const newParams = { ...queryParams };
      delete newParams.id;
      const newSearch = queryString.stringify(newParams);
      history.replace({
        pathname: location.pathname,
        search: newSearch ? `?${newSearch}` : '',
      });
    }
  };

  const handleEdit = () => {
    if (!itemData) return;

    // Only support editing events (not tasks) for now
    if (isTask) {
      addToast(false, toastConstant.toasterType.INFO, 'Not Supported', 'Task editing from calendar is not yet supported');
      return;
    }

    // For recurring events, calculate instance-specific dates if a specific date was clicked
    const instanceDates =
      itemData.is_recurring && modals.selectedDate
        ? calculateRecurringInstanceDates(itemData, modals.selectedDate, userDetails?.timezone)
        : null;

    // Pass API data directly to form - form logic will handle the mapping
    const eventForForm = {
      ...itemData,
      id: itemData.pk || itemData.event_id || activeItemId,
      // Override dates with instance-specific dates for recurring events
      ...(instanceDates && {
        meeting_start_time: instanceDates.start,
        meeting_end_time: instanceDates.end,
        when: instanceDates.start,
        till: instanceDates.end,
      }),
    };

    handleClose();
    openEventFormModal('edit', eventForForm, modals.selectedDate);
  };

  // Handle recurring event delete
  const handleDeleteRecurringEvent = async () => {
    const result = await showConfirmDialog({
      footer: {
        cancelLable: 'Cancel',
        okLable: 'Delete',
      },
      formFields: [
        {
          type: 'title',
          label: 'Delete Event',
        },
        {
          name: 'selection',
          type: 'radiobutton',
          label: 'Option',
          required: true,
          categories: [
            { key: 'current', label: 'This event' },
            { key: 'futures', label: 'This and following events' },
            { key: 'all', label: 'All recurring events' },
          ],
        },
      ],
    }).catch((error) => console.error('Dialog closed or error:', error));

    if (!result) return;
    handleDelete(true, result?.selection, modals.selectedDate);
  };

  const confirmDeleteEvent = () => {
    confirmDialog({
      message: 'Are you sure you want to delete this event? This action cannot be undone.',
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: () => {
        handleDelete();
      },
    });
  };

  const handleDelete = (isRecurringEvent = false, recurrenceScope = '', overridenStartDate = '') => {
    if (!activeItemId) return;
    const [tenantId] = getTenantDetails();

    // // Prepare delete options for recurring events
    const deleteOptions = {
      is_lead: itemData?.is_lead || false,
      is_recurring: isRecurringEvent,
      recurrence_scope: recurrenceScope,
      overriden_start_date: itemData?.all_day
        ? moment.tz(overridenStartDate, userDetails?.timezone).format('YYYY-MM-DD')
        : moment.tz(overridenStartDate, userDetails?.timezone).toISOString(),
    };

    deleteEvent(
      {
        eventId: activeItemId?.replace(isRecurringEvent ? 'event_rrule' : 'event', tenantId),
        options: isRecurringEvent ? deleteOptions : {},
      },
      {
        onSuccess: () => {
          // Close modal after successful deletion
          handleClose();
          // Remove the single event detail query cache
          queryClient.removeQueries({ queryKey: queryKeys.calendarEvents.detail(activeItemId), exact: true });
          // Invalidate calendar events to ensure fresh data
          queryClient.invalidateQueries({ queryKey: queryKeys.calendarEvents.all });
          addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, `Event deleted successfully`);
        },
        onError: (error) => {
          addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, error?.message || `Failed to delete event`);
        },
      }
    );
  };

  // Permission checks
  const isFirmWide = Boolean(itemData?.firm_wide || itemData?.is_firm_event);
  const isFirmAdmin = userDetails?.access_level === 'firm-admin' || userDetails?.adminPrivileges;

  const cancelEventPermission =
    getPermission?.('cases') === constants.permissions.WRITE && getFirmPermission?.(constants.permissions.DELETE_ITEMS);

  const editEventPermission = getPermission?.('cases') === constants.permissions.WRITE;

  // Menu items for non-recurring events
  const menuItems = [
    {
      label: 'Edit Event',
      command: handleEdit,
      disabled: !editEventPermission || (isFirmWide && !isFirmAdmin) || isDeleting,
    },
    {
      label: 'Delete Event',
      command: confirmDeleteEvent,
      disabled: !cancelEventPermission || (isFirmWide && !isFirmAdmin) || isDeleting,
    },
  ];

  // Menu items for recurring events
  const menuItemsRecurring = [
    {
      label: 'Edit Event',
      command: handleEdit,
      disabled: !editEventPermission || (isFirmWide && !isFirmAdmin) || isDeleting,
    },
    {
      label: 'Delete Event',
      command: handleDeleteRecurringEvent,
      disabled: !cancelEventPermission || (isFirmWide && !isFirmAdmin) || isDeleting,
    },
  ];

  // Loading state
  if (modals.eventView && isLoading) {
    return (
      <Dialog
        visible
        style={{ width: '700px', minHeight: '200px' }}
        className="new-task-popup media-popup event-view-modal"
        footer={<></>}
        header={ModalHeader}
        resizable={false}
        modal
      >
        <div className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: '200px' }}>
          <i className="pi pi-spin pi-spinner" style={{ fontSize: '3rem' }}></i>
          <p className="mt-3 text-muted">Fetching details...</p>
        </div>
      </Dialog>
    );
  }

  // Error state
  if (modals.eventView && (isError || !(Object.keys(itemData).length > 0))) {
    return (
      <Dialog
        header={ModalHeader}
        visible
        footer={<></>}
        onHide={handleClose}
        style={{ width: '600px' }}
        className="new-task-popup media-popup event-view-modal"
        modal
      >
        <div className="text-center p-4">
          <i className="pi pi-exclamation-triangle" style={{ fontSize: '3rem', color: '#f59e0b' }}></i>
          <p className="mt-3">
            {error?.response?.status === 404 ? 'Item not found. It may have been deleted.' : 'Unable to load details. Please try again.'}
          </p>
          {error?.message && <p className="text-muted small mt-2">{error.message}</p>}
        </div>
        <div className="d-flex justify-content-end">
          <Button label="Close" onClick={handleClose} className="p-button p-button-secondary" />
        </div>
      </Dialog>
    );
  }

  // Don't render if modal is not open or no data
  if (!modals.eventView || !itemData) {
    return null;
  }

  // Transform API data for event display
  const displayEvent = isEvent
    ? (() => {
        // For recurring events, calculate instance-specific dates if a specific date was clicked
        const instanceDates =
          itemData.is_recurring && modals.selectedDate
            ? calculateRecurringInstanceDates(itemData, modals.selectedDate, userDetails?.timezone)
            : {
                start: itemData.meeting_start_time || itemData.when,
                end: itemData.meeting_end_time || itemData.till,
              };

        return {
          id: itemData.pk || activeItemId,
          title: itemData.title || itemData.event_title || 'Untitled Event',
          start: instanceDates.start,
          end: instanceDates.end,
          allDay: itemData.all_day || false,
          extendedProps: {
            ...itemData,
            eventType: itemData.event_type,
            location: itemData.location,
            description: itemData.description,
            isFirmWide: itemData.is_firm_event || itemData.firm_wide || false,
            isRecurring: itemData.is_recurring || false,
            practiceArea: itemData.practice_area,
            caseId: itemData.case_id,
            case_name: itemData.case_name,
            conference_url: itemData.conference_url || itemData.conferencing_url,
            conference_room_id: itemData.conference_room_id,
            conference_room_name: itemData.conference_room_name,
            firm_users: itemData.guests?.filter((guest) => guest?.firm_user_id) || [],
            contacts: itemData.contacts || [],
            groups: itemData.groups || [],
            attachments: itemData.attachments || [],
            reminder: itemData.reminder || [],
            recurrence_keys: itemData.recurrence_keys,
            created_by: itemData.created_by,
            created_by_name:
              itemData.created_by_name || itemData.created_by_details?.first_name + ' ' + itemData.created_by_details?.last_name,
            created_at: itemData.created_at,
            updated_at: itemData.updated_at,
          },
        };
      })()
    : null;

  return (
    <>
      <Dialog
        visible={modals.eventView}
        onHide={handleClose}
        style={{ width: '700px', maxHeight: '90vh' }}
        className="new-task-popup media-popup event-view-modal"
        footer={<></>}
        header={ModalHeader}
        resizable={false}
        modal
      >
        {/* Loading Overlay for Delete Operation */}
        {isDeleting && (
          <div
            className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column justify-content-center align-items-center bg-white bg-opacity-75"
            style={{ zIndex: 1000 }}
          >
            <i className="pi pi-spin pi-spinner" style={{ fontSize: '3rem' }}></i>
            <p className="mt-3 text-bold">Deleting event...</p>
          </div>
        )}

        {/* Modal Heading with Three-Dot Menu */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="modal-heading F-size16 text-bold">{isEvent ? 'Event Details' : isTask ? 'Task Details' : 'Details'}</div>

          {/* Three-dot menu for events only (hidden for Google events) */}
          {isEvent && itemData?.pk && !isTask && !itemData?.is_google_event && (
            <div className="pointer">
              <div
                className="d-flex border border-1 rounded justify-content-center p-2 task-lane-header-options pointer"
                onClick={(e) => menuRef?.current?.toggle(e)}
              >
                <i className="pi pi-ellipsis-v d-flex align-items-center" />
              </div>
              <Menu model={itemData?.is_recurring ? menuItemsRecurring : menuItems} popup ref={menuRef} id="popup_menu" />
            </div>
          )}
        </div>

        {/* Event Details */}
        {isEvent && <EventDetailsView event={displayEvent} timezone={userDetails?.timezone} />}

        {/* Task Details */}
        {isTask && <TaskDetailsView task={itemData} timezone={userDetails?.timezone} />}

        {/* Unknown Type */}
        {!isEvent && !isTask && (
          <div className="text-center p-4">
            <i className="pi pi-info-circle" style={{ fontSize: '2rem', color: '#6b7280' }}></i>
            <p className="mt-3">Unknown item type: {itemData.res_type}</p>
          </div>
        )}
      </Dialog>
    </>
  );
};

export default EventViewModal;
